import React, { Fragment, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button, Dropdown, DropdownItem } from 'common/components';

import styles from './CallAnalyticsMultiSelectFilter.module.scss';

type FilterOption = {
    value: string;
    label: string;
    category?: string;
};

export interface CallAnalyticsMultiSelectFilterProps {
    label: string;
    options: FilterOption[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    allOptionLabel?: string;
    isMulti?: boolean;
    isSearchable?: boolean;
    'aria-label'?: string;
}

type GroupedFilterOptions = {
    category: string | null;
    options: FilterOption[];
};

const groupOptionsByCategory = (options: FilterOption[]): GroupedFilterOptions[] => {
    const groups = new Map<string | null, FilterOption[]>();

    for (const option of options) {
        const category = option.category?.trim() || null;
        const existingGroup = groups.get(category);

        if (existingGroup) {
            existingGroup.push(option);
            continue;
        }

        groups.set(category, [option]);
    }

    return Array.from(groups, ([category, groupedOptions]) => ({
        category,
        options: groupedOptions,
    }));
};

const isOptionSelected = (selectedValues: string[], value: string, isMulti: boolean): boolean => {
    if (value === '') {
        return selectedValues.length === 0;
    }

    if (isMulti) {
        return selectedValues.includes(value);
    }

    return selectedValues[0] === value;
};

const applyOptionChange = (selectedValues: string[], value: string, isMulti: boolean): string[] => {
    if (value === '') {
        return [];
    }

    if (isMulti) {
        if (selectedValues.includes(value)) {
            return selectedValues.filter((selectedValue) => selectedValue !== value);
        }

        return [...selectedValues, value];
    }

    return [value];
};

export const CallAnalyticsMultiSelectFilter: React.FC<CallAnalyticsMultiSelectFilterProps> = ({
    label,
    options,
    selectedValues,
    onChange,
    allOptionLabel = 'All',
    isMulti = true,
    isSearchable = false,
    'aria-label': ariaLabel,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const buttonRef = useRef<HTMLButtonElement | null>(null);

    const filterOptions = useMemo(() => [{ value: '', label: allOptionLabel }, ...options], [allOptionLabel, options]);

    const normalizedSelectedValues = useMemo(() => {
        const nonEmptyValues = selectedValues.filter(Boolean);

        if (!isMulti) {
            return nonEmptyValues.slice(0, 1);
        }

        return nonEmptyValues;
    }, [isMulti, selectedValues]);

    const filteredOptions = useMemo(() => {
        const normalizedSearch = searchValue.trim().toLowerCase();

        if (!normalizedSearch) {
            return filterOptions;
        }

        return filterOptions.filter((option) => option.label.toLowerCase().includes(normalizedSearch));
    }, [filterOptions, searchValue]);

    const groupedOptions = useMemo(() => groupOptionsByCategory(filteredOptions), [filteredOptions]);
    const hasSelection = normalizedSelectedValues.length > 0;

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.stopPropagation();
        setIsOpen((previousOpen) => !previousOpen);
    };

    const handleClose = (): void => {
        setIsOpen(false);
        setSearchValue('');
    };

    const handleOptionClick = (value: string | number): void => {
        onChange(applyOptionChange(normalizedSelectedValues, String(value), isMulti));

        if (!isMulti) {
            handleClose();
        }
    };

    return (
        <div className={styles.filter}>
            <Button
                ref={buttonRef}
                variant='outline-secondary'
                size='md'
                icon={ChevronDown}
                iconPosition='right'
                onClick={handleToggle}
                aria-label={ariaLabel ?? `${label} filter`}
                aria-expanded={isOpen}
                className={`${styles.buttonContent} ${hasSelection && styles.activeButton}`}
            >
                <span>{label}</span>
                <span className={styles.indicator}>{normalizedSelectedValues.length}</span>
            </Button>

            <Dropdown
                isOpen={isOpen}
                onClose={handleClose}
                toggleRef={buttonRef}
                selected={!isMulti ? normalizedSelectedValues[0] : undefined}
                selectedValues={isMulti ? normalizedSelectedValues : undefined}
                searchPlaceholder={isSearchable ? 'Search...' : undefined}
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                className={styles.dropdown}
            >
                {groupedOptions.map(({ category, options: categoryOptions }) => (
                    <Fragment key={category ?? '__none__'}>
                        {category ? (
                            <div className={styles.categoryLabel} role='presentation'>
                                {category}
                            </div>
                        ) : null}
                        {categoryOptions.map((option) => (
                            <DropdownItem
                                key={option.value || 'all'}
                                label={option.label}
                                value={option.value}
                                isSelected={isOptionSelected(normalizedSelectedValues, option.value, isMulti)}
                                selectionMode={isMulti ? 'multi' : 'single'}
                                onClick={handleOptionClick}
                            />
                        ))}
                    </Fragment>
                ))}
            </Dropdown>
        </div>
    );
};
